import { DownOutlined, SearchOutlined, UndoOutlined } from "@ant-design/icons";
import { AnimationVariants, TapAnimation } from "@di-devs/base-components";
import { Button, Col, Divider, Form, Input, Row, Select } from "antd";
import { motion } from "framer-motion";
import type React from "react";
import { useState } from "react";
import { DEFAULT_MEMBERS_FILTERS_STATE } from "../../utils/memberHelpers";
import {
	CollapsibleDynamicFilters,
	CustomDatePicker,
} from "./MemberBrowseFilters.styles";

const { Option } = Select;
const MotionRowComponent = motion(Row);
const MotionColComponent = motion(Col);

interface MemberBrowseFiltersProps {
	filters: any;
	setFilters: (filters: any) => void;
	memberAttributes?: any[];
	attributeFiltersComponent?: React.ComponentType<{
		memberAttributes: any[];
	}>;
}

export const MemberBrowseFilters: React.FC<MemberBrowseFiltersProps> = ({
	filters,
	setFilters,
	memberAttributes = [],
	attributeFiltersComponent: AttributeFiltersComponent,
}) => {
	const [memberFilterForm] = Form.useForm();
	const [customFiltersIsCollapsed, setCustomFiltersIsCollapsed] =
		useState(true);

	// Determine if collapsible is needed (when attributes exist)
	const displayCollapsible =
		memberAttributes.length > 0 && AttributeFiltersComponent;

	const handleFilter = (data: any) => {
		const newFilters: { [key: string]: any } = {};

		Object.keys(data).forEach((key) => {
			if (key.includes("Min") && data[key]?.$isDayjsObject) {
				newFilters[key] = data[key]
					? new Date(data[key].startOf("day"))
					: data[key];
			} else if (key.includes("Max") && data[key]?.$isDayjsObject) {
				newFilters[key] = data[key]
					? new Date(data[key].endOf("day"))
					: data[key];
			} else {
				newFilters[key] = data[key];
			}
		});

		setFilters(newFilters);
	};

	const clearFilter = () => {
		const newFilters: { [key: string]: any } = {};
		Object.keys(filters).forEach((key) => {
			newFilters[key] = undefined;
		});
		setFilters(newFilters);
		memberFilterForm.resetFields();
	};

	return (
		<Form
			form={memberFilterForm}
			name="memberFilterForm"
			onFinish={handleFilter}
			size="large"
			initialValues={DEFAULT_MEMBERS_FILTERS_STATE}
		>
			<Row gutter={[8, 8]} justify="end" className="mb-4">
				<Col>
					<TapAnimation>
						<Button type="dashed" icon={<UndoOutlined />} onClick={clearFilter}>
							フィルタークリア
						</Button>
					</TapAnimation>
				</Col>
			</Row>

			{/* Core Filters */}
			<MotionRowComponent
				gutter={[16, 16]}
				justify="start"
				variants={AnimationVariants.StaggerContainer}
				initial="hidden"
				animate="show"
				exit="hidden"
			>
				{/* LINE Name Filter */}
				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item help="LINE名" name="lineNameFilter" className="mb-4">
						<Input placeholder="例：山田" allowClear />
					</Form.Item>
				</MotionColComponent>

				{/* Registration Status Filter */}
				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item
						help="会員登録"
						name="isRegisteredFilter"
						className="mb-4 w-full"
					>
						<Select placeholder="会員登録あり／会員登録なし" allowClear>
							<Option value={true}>会員登録あり</Option>
							<Option value={false}>会員登録なし</Option>
						</Select>
					</Form.Item>
				</MotionColComponent>

				{/* Block Status Filter */}
				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item
						help="ブロック"
						name="isFriendsFilter"
						className="mb-4 w-full"
					>
						<Select
							placeholder="ブロックユーザーのみ／ブロックユーザーを含まない"
							allowClear
						>
							<Option value={0}>ブロックユーザーのみ</Option>
							<Option value={1}>ブロックユーザーを含まない</Option>
						</Select>
					</Form.Item>
				</MotionColComponent>

				{/* Chat Status Filter */}
				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item
						help="LINEチャット"
						name="messagesFilter"
						className="mb-4 w-full"
					>
						<Select placeholder="未読／既読" allowClear>
							<Option value="unread">未読</Option>
							<Option value="read">既読</Option>
						</Select>
					</Form.Item>
				</MotionColComponent>

				{/* Friend Registration Date Filters */}
				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item
						help="友だち追加日から"
						name="createdAtMinFilter"
						className="mb-4"
					>
						<CustomDatePicker className="w-full" inputReadOnly />
					</Form.Item>
				</MotionColComponent>

				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item
						help="友だち追加日まで"
						name="createdAtMaxFilter"
						className="mb-4 w-full"
					>
						<CustomDatePicker className="w-full" inputReadOnly />
					</Form.Item>
				</MotionColComponent>

				{/* Member Registration Date Filters */}
				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item
						help="会員登録日から"
						name="memberSinceMinFilter"
						className="mb-4"
					>
						<CustomDatePicker className="w-full" inputReadOnly />
					</Form.Item>
				</MotionColComponent>

				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item
						help="会員登録日まで"
						name="memberSinceMaxFilter"
						className="mb-4 w-full"
					>
						<CustomDatePicker className="w-full" inputReadOnly />
					</Form.Item>
				</MotionColComponent>

				{/* Last Visit Date Filters */}
				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item
						help="最終来店日から"
						name="lastVisitMinFilter"
						className="mb-4"
					>
						<CustomDatePicker className="w-full" inputReadOnly />
					</Form.Item>
				</MotionColComponent>

				<MotionColComponent
					variants={AnimationVariants.StaggerItem}
					xs={{ span: 24 }}
					md={{ span: 12 }}
					lg={{ span: 6 }}
					xl={{ span: 6 }}
				>
					<Form.Item
						help="最終来店日まで"
						name="lastVisitMaxFilter"
						className="mb-4 w-full"
					>
						<CustomDatePicker className="w-full" inputReadOnly />
					</Form.Item>
				</MotionColComponent>

				<Divider className="mt-0" />
			</MotionRowComponent>

			{/* Dynamic Attribute Filters (if provided) */}
			{displayCollapsible && (
				<MotionRowComponent
					gutter={[16, 16]}
					justify="start"
					variants={AnimationVariants.StaggerContainer}
					initial="hidden"
					animate="show"
					exit="hidden"
				>
					<CollapsibleDynamicFilters
						ghost
						items={[
							{
								key: "dynamicFiltersCollapse1",
								label: customFiltersIsCollapsed ? "もっと見る" : "閉じる",
								showArrow: true,
								forceRender: true,
								children: (
									<MotionRowComponent
										gutter={[16, 16]}
										justify="start"
										variants={AnimationVariants.StaggerContainer}
										initial="hidden"
										animate="show"
										exit="hidden"
										className="order-1"
									>
										<AttributeFiltersComponent
											memberAttributes={memberAttributes}
										/>
									</MotionRowComponent>
								),
							},
						]}
						className="w-full"
						expandIcon={({ isActive }) => (
							<DownOutlined rotate={isActive ? 180 : 0} />
						)}
						onChange={() =>
							setCustomFiltersIsCollapsed(!customFiltersIsCollapsed)
						}
					/>
				</MotionRowComponent>
			)}

			<Row gutter={[8, 8]} justify="center" className="mt-4">
				<Col>
					<TapAnimation>
						<Button
							icon={<SearchOutlined />}
							type="primary"
							htmlType="submit"
							className="w-32"
						>
							検索
						</Button>
					</TapAnimation>
				</Col>
			</Row>
		</Form>
	);
};
